import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress, Select,
    Input, Modal, ModalDialog, ModalClose, Alert, IconButton, Option
} from '@mui/joy';
import { motion } from 'framer-motion';

import ArrowBackRoundedIcon     from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon           from '@mui/icons-material/AddRounded';
import EditRoundedIcon          from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon        from '@mui/icons-material/DeleteRounded';
import LocationOnRoundedIcon    from '@mui/icons-material/LocationOnRounded';
import LocationOffRoundedIcon   from '@mui/icons-material/LocationOffRounded';
import SearchRoundedIcon        from '@mui/icons-material/SearchRounded';
import PlaceRoundedIcon         from '@mui/icons-material/PlaceRounded';
import SettingsRoundedIcon      from '@mui/icons-material/SettingsRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetLocations,
    APICreateLocation,
    APIUpdateLocation,
    APIDeleteLocation,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

import { payrollRequest } from '../../payrollApi';

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.055 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};


function FormField({ label, children }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

// ─── Компонент: карточка локации ──────────────────────────────────────────────
function LocationCard({ location, onEdit, onDelete, onToggleActive, canUpdate, canDelete, canCreate }) {
    const active = location.is_active;

    return (
        <motion.div variants={fadeUp}>
            <Box sx={{
                display:      'flex',
                alignItems:   'center',
                gap:          1.5,
                px:           2,
                py:           1.6,
                borderRadius: 'var(--joy-radius-xl)',
                border:       '1px solid',
                borderColor:  active ? 'var(--joy-palette-divider)' : 'rgba(248,113,113,0.2)',
                bgcolor:      active ? 'var(--joy-palette-background-level1)' : 'rgba(248,113,113,0.03)',
                transition:   'all .15s',
                opacity:      active ? 1 : 0.75,
                '&:hover':    {
                    borderColor: active ? 'rgba(162,38,226,0.3)' : 'rgba(248,113,113,0.35)',
                    bgcolor:     active ? 'var(--joy-palette-background-level2)' : 'rgba(248,113,113,0.07)',
                },
            }}>
                {/* Иконка */}
                <Box sx={{
                    width: 38, height: 38, borderRadius: 'var(--joy-radius-md)',
                    bgcolor: active ? 'rgba(162,38,226,0.12)' : 'rgba(248,113,113,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    {active
                        ? <LocationOnRoundedIcon  sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />
                        : <LocationOffRoundedIcon sx={{ fontSize: 18, color: '#f87171' }} />
                    }
                </Box>

                {/* Инфо */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)', lineHeight: 1.2 }}>
                            {location.name}
                        </Typography>
                        {!active && (
                            <Chip size="sm" sx={{ bgcolor: 'rgba(248,113,113,0.1)', color: '#f87171', fontSize: 10, border: 'none' }}>
                                Неактивна
                            </Chip>
                        )}
                    </Box>
                    {location.address && (
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.3, display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <PlaceRoundedIcon sx={{ fontSize: 11 }} />
                            {location.address}
                        </Typography>
                    )}
                    {location.salary_config_name && (
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-primary-400)', mt: 0.2, display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <SettingsRoundedIcon sx={{ fontSize: 11 }} />
                            Конфигурация: {location.salary_config_name}
                            {location.has_administrators && ' · Есть администраторы'}
                        </Typography>
                    )}
                </Box>

                {/* Кнопки */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {canUpdate && (
                        <IconButton
                            size="sm"
                            variant="soft"
                            title={active ? 'Деактивировать' : 'Активировать'}
                            onClick={() => onToggleActive(location)}
                            sx={{
                                bgcolor:   active ? 'rgba(34, 197, 94, 0.75)' : 'rgba(162,38,226,0.08)',
                                color:     active ? '#22c55e' : 'var(--joy-palette-primary-400)',
                                borderRadius: 'var(--joy-radius-md)',
                                '&:hover': { bgcolor: active ? 'rgba(34, 197, 94, 0.6)' : 'rgba(162,38,226,0.18)' },
                            }}
                        >
                            {active
                                ? <LocationOffRoundedIcon sx={{ fontSize: 20, color: 'white' }} />
                                : <LocationOnRoundedIcon  sx={{ fontSize: 20, color: 'white' }} />
                            }
                        </IconButton>
                    )}
                    {canUpdate && (
                        <IconButton
                            size="sm"
                            variant="soft"
                            onClick={() => onEdit(location)}
                            sx={{ bgcolor: 'rgba(162,38,226,0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(162,38,226,0.18)' } }}
                        >
                            <EditRoundedIcon sx={{ fontSize: 20, color: 'primary.300' }} />
                        </IconButton>
                    )}
                    {canDelete && (
                        <IconButton
                            size="sm"
                            variant="soft"
                            onClick={() => onDelete(location)}
                            sx={{ bgcolor: 'rgba(248,113,113,0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(248,113,113,0.18)' } }}
                        >
                            <DeleteRoundedIcon sx={{ fontSize: 20, color: '#f87171' }} />
                        </IconButton>
                    )}
                </Box>
            </Box>
        </motion.div>
    );
}

// ─── Модалка: создать / редактировать локацию ─────────────────────────────────
function LocationModal({ open, location, onClose, onSave, loading, error }) {
    const { token, hasPerm } = useContext(AppContext);
    const canCopySalary = hasPerm('finance.configs.create') && hasPerm('finance.configs.view');
    const isEdit = !!location;
    const [form, setForm] = useState({ name: '', address: '', is_active: true });
    const [errs, setErrs] = useState({});
    const [copyLocations, setCopyLocations] = useState([]);
    const [copyError, setCopyError] = useState('');
    const [copyFromId, setCopyFromId] = useState('');

    useEffect(() => {
        if (open) {
            setCopyFromId(''); setErrs({});
            setForm({
                name:      location?.name      || '',
                address:   location?.address   || '',
                is_active: location?.is_active ?? true,
            });
        }
    }, [open, location]);

    const set = field => e => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
        setErrs(prev => ({ ...prev, [field]: undefined }));
    };

    useEffect(() => {
        setCopyLocations([]); setCopyError('');
        if (!open || !token || !canCopySalary || isEdit) return undefined;
        const controller = new AbortController();
        payrollRequest(token, 'locations/', { signal: controller.signal }).then(list => {
            if (!controller.signal.aborted) setCopyLocations(list.filter(item => item.salary_config));
        }).catch(err => { if (!controller.signal.aborted) setCopyError(err.message); });
        return () => controller.abort();
    }, [open, token, canCopySalary, isEdit]);

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                maxWidth: 420, width: '100%', p: 3,
                maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
            }}>
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', letterSpacing: '-0.01em' }}>
                        {isEdit ? 'Редактировать точку' : 'Новая точка'}
                    </Typography>
                </Box>

                <Stack spacing={1.8}>
                    <FormField label="Название *">
                        <Input
                            value={form.name}
                            onChange={set('name')}
                            placeholder="Торговый центр Мега"
                        />
                    </FormField>

                    <FormField label="Адрес">
                        <Input
                            value={form.address}
                            onChange={set('address')}
                            placeholder="ул. Примерная, д. 1"
                            startDecorator={<PlaceRoundedIcon sx={{ fontSize: 15, color: 'var(--joy-palette-text-tertiary)' }} />}
                        />
                    </FormField>

                    {!isEdit && (
                        <FormField label="Расчёт зарплаты *">
                            <Stack spacing={1}>
                                {/* Вариант 1 — Без конфигурации */}
                                <Box
                                    onClick={() => setCopyFromId('')}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 'var(--joy-radius-md)',
                                        border: '1.5px solid',
                                        borderColor: copyFromId === '' ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                        bgcolor: copyFromId === '' ? 'rgba(162,38,226,0.06)' : 'var(--joy-palette-background-level2)',
                                        cursor: 'pointer',
                                        transition: 'all .15s',
                                    }}
                                >
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                        🌟 Использовать конфигурацию по умолчанию
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                                        При создании точка получит исходные общие условия. Дальнейшие назначения меняются в разделе «Условия расчёта» с выбором даты.
                                    </Typography>
                                </Box>

                                {copyError && <Alert color="warning">Не удалось загрузить точки для копирования: {copyError} Закрой и открой окно для повтора.</Alert>}
                                {/* Вариант 2 — Копировать с существующей */}
                                {canCopySalary && copyLocations.length > 0 && (
                                    <Box sx={{ pl: 0 }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Или скопировать с точки
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            {copyLocations.map(c => (
                                                <Box
                                                    key={c.id}
                                                    onClick={() => setCopyFromId(c.id)}
                                                    sx={{
                                                        p: 1.2,
                                                        borderRadius: 'var(--joy-radius-sm)',
                                                        border: '1.5px solid',
                                                        borderColor: copyFromId === c.id ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                                        bgcolor: copyFromId === c.id ? 'rgba(162,38,226,0.06)' : 'transparent',
                                                        cursor: 'pointer', transition: 'all .15s',
                                                        '&:hover': { borderColor: 'var(--joy-palette-primary-300)' },
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-primary)' }}>
                                                        {c.name}{c.salary_config_name ? ` · ${c.salary_config_name}` : ''}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}

                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                                    После создания точку можно будет настроить отдельно в разделе «Условия расчёта».
                                </Typography>
                            </Stack>
                        </FormField>
                    )}

                    {isEdit && (
                        <FormField label="Статус">
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {[
                                    { val: true,  label: 'Активна',   color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
                                    { val: false, label: 'Неактивна', color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
                                ].map(opt => (
                                    <Box
                                        key={String(opt.val)}
                                        onClick={() => setForm(p => ({ ...p, is_active: opt.val }))}
                                        sx={{
                                            flex: 1, textAlign: 'center', py: 1,
                                            borderRadius: 'var(--joy-radius-md)',
                                            border: '1.5px solid',
                                            borderColor: form.is_active === opt.val ? opt.color : 'var(--joy-palette-divider)',
                                            bgcolor: form.is_active === opt.val ? opt.bg : 'transparent',
                                            cursor: 'pointer', transition: 'all .15s',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: form.is_active === opt.val ? opt.color : 'var(--joy-palette-text-secondary)' }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </FormField>
                    )}

                    {error && (
                        <Alert color="danger" variant="soft" size="sm" sx={{ borderRadius: 'var(--joy-radius-md)', fontSize: 12 }}>
                            {typeof error === 'string' ? error : JSON.stringify(error)}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
                        <Button
                            onClick={() => {
                                const e = {};
                                if (!form.name?.trim()) e.name = 'Укажи название точки';
                                setErrs(e);
                                if (Object.keys(e).length === 0) {
                                    const payload = { ...form };
                                    if (!isEdit && canCopySalary && copyFromId) {
                                        payload.salary_config = { copy_from_location_id: Number(copyFromId) };
                                    }
                                    onSave(payload);
                                }
                            }}
                            loading={loading}
                            sx={{
                                flex: 1,
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700, border: 'none',
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                            }}
                        >
                            {isEdit ? 'Сохранить' : 'Создать'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)', fontWeight: 600 }}
                        >
                            Отмена
                        </Button>
                    </Box>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}

// ─── Модалка: удаление ────────────────────────────────────────────────────────
function DeleteModal({ open, location, onClose, onConfirm, loading }) {
    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid rgba(248,113,113,0.3)',
                borderRadius: 'var(--joy-radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                maxWidth: 360, width: '100%', p: 3,
                maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
            }}>
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box sx={{
                        width: 52, height: 52, borderRadius: '50%',
                        bgcolor: 'rgba(248,113,113,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 1.5,
                    }}>
                        <DeleteRoundedIcon sx={{ fontSize: 24, color: '#f87171' }} />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                        Отключить точку?
                    </Typography>
                    {location && (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                            «{location.name}» пропадёт из списков выбора. Все данные —
                            чеки, назначения, история — сохранятся. Вернуть точку можно
                            переключателем активности.
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={onConfirm}
                        loading={loading}
                        sx={{
                            flex: 1,
                            background: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                            color: '#fff', fontWeight: 700, border: 'none',
                            boxShadow: '0 4px 16px rgba(248,113,113,0.35)',
                        }}
                    >
                        Отключить
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)', fontWeight: 600 }}
                    >
                        Отмена
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}

// ════════════════════════════════════════════════════════════════════
//  LocationsPage
// ════════════════════════════════════════════════════════════════════
export default function LocationsPage() {
    const { token, showToast, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate = hasPerm('locations.create');
    const canUpdate = hasPerm('locations.update');
    const canDelete = hasPerm('locations.delete');

    const [locations,   setLocations]   = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState('');
    const [filter,      setFilter]      = useState('all'); // all | active | inactive

    const [editModal,   setEditModal]   = useState({ open: false, location: null, loading: false, error: '' });
    const [deleteModal, setDeleteModal] = useState({ open: false, location: null, loading: false });

    const loadLocations = useCallback(async () => {
        setLoading(true);
        const data = await APIGetLocations(token);
        if (Array.isArray(data)) setLocations(data);
        setLoading(false);
    }, [token]);

    useEffect(() => { loadLocations(); }, [loadLocations]);

    // ── Фильтрация ────────────────────────────────────────────────
    const filtered = locations.filter(l => {
        const q = search.toLowerCase();
        const matchSearch = !q || l.name.toLowerCase().includes(q) || l.address?.toLowerCase().includes(q);
        const matchFilter = filter === 'all'
            || (filter === 'active' && l.is_active)
            || (filter === 'inactive' && !l.is_active);
        return matchSearch && matchFilter;
    });

    const activeCount   = locations.filter(l => l.is_active).length;
    const inactiveCount = locations.filter(l => !l.is_active).length;

    // ── Сохранить ────────────────────────────────────────────────
    const handleSave = async (form) => {
        const isEdit = !!editModal.location;
        setEditModal(prev => ({ ...prev, loading: true, error: '' }));

        const res = isEdit
            ? await APIUpdateLocation(token, editModal.location.id, form)
            : await APICreateLocation(token, form);

        if (res?.error) {
            const msg = typeof res.error === 'string'
                ? res.error
                : Object.values(res.error).flat().join('; ');
            setEditModal(prev => ({ ...prev, loading: false, error: msg }));
            return;
        }

        showToast(isEdit ? 'Точка обновлена ✓' : 'Точка создана ✓');
        setEditModal({ open: false, location: null, loading: false, error: '' });
        loadLocations();
    };

    // ── Переключить активность ────────────────────────────────────
    const handleToggleActive = async (loc) => {
        const res = await APIUpdateLocation(token, loc.id, { is_active: !loc.is_active });
        if (!res?.error) {
            showToast(loc.is_active ? 'Точка деактивирована' : 'Точка активирована ✓', loc.is_active ? 'warning' : 'success');
            loadLocations();
        } else {
            showToast('Ошибка при обновлении', 'danger');
        }
    };

    // ── Удалить ───────────────────────────────────────────────────
    const handleDelete = async () => {
        setDeleteModal(prev => ({ ...prev, loading: true }));
        const res = await APIDeleteLocation(token, deleteModal.location.id);
        setDeleteModal({ open: false, location: null, loading: false });
        if (!res?.error) {
            showToast('Точка отключена — история сохранена', 'warning');
            loadLocations();
        } else {
            showToast('Ошибка при удалении', 'danger');
        }
    };

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* Заголовок */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                onClick={() => navigate('/manager')}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 36, height: 36,
                                    borderRadius: 'var(--joy-radius-md)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    cursor: 'pointer', transition: 'all .15s',
                                    '&:hover': { borderColor: 'var(--joy-palette-primary-500)', bgcolor: 'rgba(162,38,226,0.08)' },
                                }}
                            >
                                <ArrowBackRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Рабочие точки
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.3 }}>
                                    <Typography sx={{ fontSize: 11, color: '#22c55e' }}>
                                        {activeCount} активных
                                    </Typography>
                                    {inactiveCount > 0 && (
                                        <Typography sx={{ fontSize: 11, color: '#f87171' }}>
                                            {inactiveCount} неактивных
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        
                        {canCreate && (
                            <Button
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setEditModal({ open: true, location: null, loading: false, error: '' })}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 13,
                                    border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                    '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                }}
                            >
                                Добавить
                            </Button>
                        )}
                    </Box>
                </motion.div>

                {/* Поиск и фильтр */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.08 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
                        <Input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Поиск по названию или адресу…"
                            startDecorator={<SearchRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-text-tertiary)' }} />}
                            sx={{
                                flex: 1, minWidth: 200,
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13, color: 'white',
                                '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.8 }}>
                            {[
                                { val: 'all',      label: 'Все' },
                                { val: 'active',   label: 'Активные' },
                                { val: 'inactive', label: 'Неактивные' },
                            ].map(opt => (
                                <Box
                                    key={opt.val}
                                    onClick={() => setFilter(opt.val)}
                                    sx={{
                                        px: 1.4, py: 0.7,
                                        borderRadius: 'var(--joy-radius-md)',
                                        border: '1px solid', alignContent: 'center',
                                        borderColor: filter === opt.val ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                        bgcolor: filter === opt.val ? 'rgba(162,38,226,0.1)' : 'var(--joy-palette-background-level1)',
                                        cursor: 'pointer', transition: 'all .15s',
                                        '&:hover': { borderColor: 'var(--joy-palette-primary-400)' },
                                    }}
                                >
                                    <Typography sx={{
                                        fontSize: 12, fontWeight: filter === opt.val ? 700 : 500,
                                        color: filter === opt.val ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-secondary)',
                                    }}>
                                        {opt.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </motion.div>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2,
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                    }}>
                        <LocationOnRoundedIcon sx={{ fontSize: 32, color: 'var(--joy-palette-text-tertiary)' }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            {search ? 'Ничего не найдено' : 'Нет точек'}
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={1}>
                            {filtered.map(loc => (
                                <LocationCard
                                    key={loc.id}
                                    location={loc}
                                    canCreate={canCreate}
                                    canUpdate={canUpdate}
                                    canDelete={canDelete}
                                    onEdit={(l) => setEditModal({ open: true, location: l, loading: false, error: '' })}
                                    onDelete={(l) => setDeleteModal({ open: true, location: l, loading: false })}
                                    onToggleActive={handleToggleActive}
                                />
                            ))}
                        </Stack>
                    </motion.div>
                )}
            </Box>

            <LocationModal
                open={editModal.open}
                location={editModal.location}
                loading={editModal.loading}
                error={editModal.error}
                onClose={() => setEditModal({ open: false, location: null, loading: false, error: '' })}
                onSave={handleSave}
            />

            <DeleteModal
                open={deleteModal.open}
                location={deleteModal.location}
                loading={deleteModal.loading}
                onClose={() => setDeleteModal({ open: false, location: null, loading: false })}
                onConfirm={handleDelete}
            />
        </motion.div>
    );
}